import * as React from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import { Checkbox, Legend, Radio } from "atoms";
import { useAuth } from "features/auth";
import type { SaleTag } from "../types";

/**
 * Array to build checkboxes for post type filter
 *
 * NOTE: value needs to be lowercased valid PostType
 */
const typeOptions = [
  { label: "Art Drops", value: "sale" },
  { label: "Announcements", value: "announcement" },
  { label: "Events", value: "event" },
  { label: "Giveaways", value: "giveaway" },
];

/**
 * Array to build checkboxes for sale type filter
 */
const saleTypeOptions: SaleTag[] = [
  "1/1",
  "Limited Edition",
  "Open Edition",
  "Collection",
  "Auction",
  "Physical Art",
  "Art Print",
  "Merchandise",
];

/**
 * Create new search params from given key/value pair of input onChange event
 */
const buildNewParams = (
  params: URLSearchParams,
  key: string,
  value: string
): URLSearchParams => {
  const result = new URLSearchParams(params);

  // Radio button for following works differently than other filters
  if (key === "following") {
    // Only add param if "true" input is selected, otherwise remove the param
    if (value === "true") result.set(key, value);
    else result.delete(key);
    return result;
  }

  // Search params don't already have that key, so add it with new value
  if (!result.has(key)) {
    result.set(key, value);

    // If we just added a new saleType, make sure `type=sale` is also present
    if (key === "saleType" && !result.getAll("type").includes("sale")) {
      result.append("type", "sale");
    }
    return result;
  }

  // Otherwise, get the current array of any and all values
  const currentVals = result.getAll(key);

  // If new value isn't present in current values, append the new value
  if (!currentVals.includes(value)) {
    result.append(key, value);
    return result;
  }

  // Value exists in current array, so filter it out and reset the remaining values
  const newVals = currentVals.filter((current) => current !== value);

  // Value was the only item in array, so remove key entirely
  if (newVals.length === 0) result.delete(key);
  // Otherwise reset the key with the first new value
  else result.set(key, newVals[0]);
  // And if there are any more, append them after the first
  newVals.slice(1).forEach((newVal) => {
    result.append(key, newVal);
  });

  // If we just removed `type=sale`, also remove any saleType filters
  if (key === "type" && value === "sale" && !newVals.includes("sale")) {
    result.delete("saleType");
  }

  return result;
};

type PostFiltersProps = {
  className?: string;
};

function PostFilters({ ...props }: PostFiltersProps) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current search param values to determine state of filter inputs
  const followingFilter = searchParams.get("following");
  const typeFilters = searchParams.getAll("type");
  const saleTypeFilters = searchParams.getAll("saleType");

  // In the off chance that a user logs out while viewing posts for artists they follow, remove that filter
  // TODO: redirect them to sign-in and back instead?
  React.useEffect(() => {
    if (!user && followingFilter) {
      setSearchParams((oldParams) => {
        oldParams.delete("following");
        return oldParams;
      });
    }
  }, [user, followingFilter, setSearchParams]);

  // Conditionally render sub-filters based on post type selection
  const showSaleTypeOption = typeFilters.includes("sale");

  // When any filter inputs change, update the URL
  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const { name, value } = event.currentTarget;
    setSearchParams(buildNewParams(searchParams, name, value));
  };

  return (
    <>
      <PostFiltersForm {...props}>
        {user && (
          <fieldset>
            <Legend>Updates from</Legend>
            <Radio
              name="following"
              value="false"
              label="All Artists"
              checked={!followingFilter}
              onChange={onChange}
            />
            <Radio
              name="following"
              value="true"
              label="Only Artists I Follow"
              checked={followingFilter === "true"}
              onChange={onChange}
            />
          </fieldset>
        )}

        <fieldset>
          <Legend>Update Category</Legend>

          {typeOptions.map(({ label, value }) => (
            <Checkbox
              key={value}
              name="type"
              label={label}
              value={value}
              checked={typeFilters.includes(value)}
              onChange={onChange}
            />
          ))}
        </fieldset>

        {showSaleTypeOption && (
          <fieldset>
            <Legend>Art Drop Type</Legend>

            {saleTypeOptions.map((value) => (
              <Checkbox
                key={value}
                name="saleType"
                label={value}
                value={value}
                checked={saleTypeFilters.includes(value)}
                onChange={onChange}
              />
            ))}
          </fieldset>
        )}
      </PostFiltersForm>
      <OverflowGradient />
    </>
  );
}

const PostFiltersForm = styled.form(({ theme }) => ({
  overflow: "auto",
  maxHeight: "80vh",
  paddingBottom: "2rem",

  "fieldset+fieldset": {
    marginTop: "2rem",
  },
  "label, legend": {
    fontSize: 15,
    lineHeight: 1.2,
    margin: 0,
  },
  "&& label": {
    margin: ".66rem 0 0 0",
  },
  "&& label+label": {
    marginBottom: 0,
  },
  "label input": {
    height: "1.25em",
    minHeight: "1.25em",
    minWidth: "1.25em",
    width: "1.25em",
  },
  "label:hover, label:focus-visible": {
    color: theme.colors.fg,
  },
}));

const OverflowGradient = styled.div(({ theme }) => ({
  backgroundImage: `linear-gradient(to bottom, rgb(0 0 0 / 0) 0%, ${theme.colors.bg} 100%)`,
  height: "2rem",
  transform: "translateY(-100%)",
  width: "95%",
}));
export { PostFilters };
